import { useState, useEffect, type CSSProperties } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { deliveriesAPI } from '../../api/deliveries'
import { usersAPI } from '../../api/users'
import type { Delivery, DeliveryStatus, TimelineEntry } from '../../types'
import DashIcon from '../../components/ui/DashIcon'
import DashAvatar from '../../components/ui/DashAvatar'
import StatusBadge from '../../components/ui/StatusBadge'
import { ColisQRCode } from '../../components/expediteur/ColisQRCode'

// ─── Constants ─────────────────────────────────────────────────────────────────

const ALL_STATUSES: { value: DeliveryStatus; label: string }[] = [
  { value: 'pending',     label: 'En attente' },
  { value: 'validated',   label: 'Validé' },
  { value: 'assigned',    label: 'Assigné' },
  { value: 'picked_up',   label: 'Récupéré' },
  { value: 'in_transit',  label: 'En route' },
  { value: 'delivered',   label: 'Livré' },
  { value: 'failed',      label: 'Échec' },
  { value: 'cancelled',   label: 'Annulé' },
  { value: 'returned',    label: 'Retourné' },
  { value: 'unreachable', label: 'Client injoinable' },
]

const TRACKING_STEPS = [
  { statuses: ['pending', 'validated', 'matching'], label: 'Commande créée',              pending: 'Création de la commande' },
  { statuses: ['accepted', 'assigned'],              label: 'Livreur trouvé',              pending: 'En attente d\'un livreur' },
  { statuses: ['picked_up'],                         label: 'Récupéré par le livreur',     pending: 'Ramassage à planifier' },
  { statuses: ['in_transit'],                        label: 'En route vers le destinataire', pending: 'Transit en cours' },
  { statuses: ['delivered'],                         label: 'Livré',                       pending: 'Signature + photo requis' },
]

const STATUS_LABEL: Record<string, string> = {
  pending: 'Commande créée', validated: 'Validé', matching: 'Matching livreur',
  accepted: 'Livreur trouvé', assigned: 'Livreur assigné', picked_up: 'Récupéré par le livreur',
  in_transit: 'En route', delivered: 'Livré', failed: 'Échec de livraison',
  cancelled: 'Annulé', returned: 'Retourné', unreachable: 'Client injoinable',
}

const SIZE_LABEL: Record<string, string> = {
  small: 'Petit', medium: 'Moyen', large: 'Grand', extra: 'Extra-large',
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })

const fmtRelative = (iso: string) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'à l\'instant'
  if (mins < 60) return `il y a ${mins} min`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `il y a ${hrs}h`
  return `il y a ${Math.floor(hrs / 24)}j`
}

function stepIndexFor(status: string): number {
  for (let i = 0; i < TRACKING_STEPS.length; i++) {
    if (TRACKING_STEPS[i].statuses.includes(status)) return i
  }
  return -1
}

function timelineEntryFor(timeline: TimelineEntry[], statuses: string[]): TimelineEntry | undefined {
  return [...timeline].reverse().find(e => statuses.includes(e.status))
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, value, sub, green, strong }: {
  label: string; value?: string | null; sub?: string | null; green?: boolean; strong?: boolean
}) {
  if (!value) return null
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '10px 0', borderBottom: '1px solid #F3F4F6',
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0, marginRight: 24, paddingTop: 2 }}>
        {label}
      </span>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 13, fontWeight: strong ? 700 : 500, color: green ? '#059669' : strong ? '#0D1117' : '#374151' }}>
          {value}
        </div>
        {sub && <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  )
}

function InfoChip({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1117' }}>{value ?? '—'}</div>
    </div>
  )
}

// ─── Assign livreur modal ──────────────────────────────────────────────────────

interface LivreurItem {
  _id: string
  name: string
  phone: string
  isOnline: boolean
  isVerified: boolean
  rating: number
  totalDeliveries: number
  vehicle?: { type?: string; plate?: string }
}

function AssignLivreurModal({ deliveryId, onClose, onSuccess }: {
  deliveryId: string; onClose: () => void; onSuccess: () => void
}) {
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [assigningId, setAssigningId] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350)
    return () => clearTimeout(t)
  }, [search])

  const { data, isLoading } = useQuery({
    queryKey: ['livreurs-assign', debounced],
    queryFn: () => usersAPI.getLivreurs({ search: debounced || undefined, limit: 30 }),
    staleTime: 30_000,
  })

  const livreurs: LivreurItem[] = (data as { data?: { data?: LivreurItem[] } } | undefined)?.data?.data ?? []

  const handleAssign = async (livreur: LivreurItem) => {
    setAssigningId(livreur._id)
    try {
      await deliveriesAPI.assign(deliveryId, livreur._id)
      toast.success(`${livreur.name} assigné avec succès`)
      onSuccess()
      onClose()
    } catch {
      toast.error('Erreur lors de l\'assignation')
    } finally {
      setAssigningId(null)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, width: 520, maxWidth: '100%', maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '22px 24px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0D1117' }}>Assigner un livreur</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', padding: 4 }}>
              <DashIcon name="x" size={18} />
            </button>
          </div>
          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '8px 12px', marginBottom: 16 }}>
            <DashIcon name="search" size={14} style={{ color: '#9CA3AF', flexShrink: 0 }} />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, téléphone…"
              style={{ flex: 1, border: 'none', background: 'none', outline: 'none', fontSize: 13, fontFamily: 'inherit', color: '#0D1117' }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 0, display: 'flex' }}>
                <DashIcon name="x" size={14} />
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF', fontSize: 13 }}>Chargement…</div>
          ) : livreurs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF', fontSize: 13 }}>
              {debounced ? `Aucun résultat pour « ${debounced} »` : 'Aucun livreur disponible'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {livreurs.map((l) => (
                <div
                  key={l._id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                    border: '1px solid #E5E7EB', borderRadius: 12, background: '#FAFAFA',
                    transition: 'border-color 0.15s',
                  }}
                >
                  {/* Avatar + online dot */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <DashAvatar name={l.name} size={40} />
                    {l.isOnline && (
                      <span style={{
                        position: 'absolute', bottom: 1, right: 1,
                        width: 9, height: 9, borderRadius: '50%',
                        background: '#10B981', border: '2px solid #fff',
                      }} />
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#0D1117', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {l.name}
                      </span>
                      {!l.isVerified && (
                        <span style={{ fontSize: 10, fontWeight: 600, background: '#FEF3C7', color: '#D97706', borderRadius: 6, padding: '1px 6px', flexShrink: 0 }}>
                          Non vérifié
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 3 }}>{l.phone}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: '#9CA3AF' }}>
                      {(l.rating ?? 0) > 0 && (
                        <span>
                          <span style={{ color: '#F59E0B' }}>★</span>{' '}
                          <span style={{ fontWeight: 600, color: '#374151' }}>{(l.rating ?? 0).toFixed(1)}</span>
                        </span>
                      )}
                      <span>· {l.totalDeliveries ?? 0} livraisons</span>
                      {l.vehicle?.type && (
                        <span style={{ textTransform: 'capitalize' }}>· {l.vehicle.type}{l.vehicle.plate ? ` (${l.vehicle.plate})` : ''}</span>
                      )}
                    </div>
                  </div>

                  {/* Status + button */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20,
                      background: l.isOnline ? '#EFF9F5' : '#F3F4F6',
                      color: l.isOnline ? '#059669' : '#9CA3AF',
                      border: `1px solid ${l.isOnline ? '#A7F3D0' : '#E5E7EB'}`,
                    }}>
                      {l.isOnline ? 'En ligne' : 'Hors ligne'}
                    </span>
                    <button
                      onClick={() => handleAssign(l)}
                      disabled={assigningId !== null}
                      style={{
                        background: assigningId === l._id ? '#E5E7EB' : '#0D1117',
                        color: assigningId === l._id ? '#9CA3AF' : '#fff',
                        border: 'none', borderRadius: 8, padding: '6px 14px',
                        fontSize: 12, fontWeight: 700, cursor: assigningId !== null ? 'not-allowed' : 'pointer',
                        fontFamily: 'inherit', transition: 'background 0.15s',
                      }}
                    >
                      {assigningId === l._id ? '…' : 'Assigner'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Status modal ──────────────────────────────────────────────────────────────

function StatusModal({ deliveryId, current, onClose, onSuccess }: {
  deliveryId: string; current: string; onClose: () => void; onSuccess: () => void
}) {
  const [status, setStatus] = useState(current)
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    setLoading(true)
    try {
      await deliveriesAPI.updateStatus(deliveryId, { status: status as DeliveryStatus, comment: comment || undefined })
      toast.success('Statut mis à jour')
      onSuccess(); onClose()
    } catch { toast.error('Erreur lors de la mise à jour') }
    finally { setLoading(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 440, maxWidth: '92vw' }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#0D1117' }}>Modifier le statut manuellement</h3>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 14, marginBottom: 12, fontFamily: 'inherit' }}
        >
          {ALL_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Commentaire (optionnel)"
          rows={3}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', marginBottom: 18, fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>
            {loading ? 'Mise à jour…' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Price modal ───────────────────────────────────────────────────────────────

function PriceModal({ deliveryId, current, onClose, onSuccess }: {
  deliveryId: string; current: number; onClose: () => void; onSuccess: () => void
}) {
  const [price, setPrice] = useState(String(current))
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    const val = parseFloat(price)
    if (isNaN(val) || val <= 0) { toast.error('Prix invalide'); return }
    setLoading(true)
    try {
      await deliveriesAPI.update(deliveryId, { 'price.base': val })
      toast.success('Frais mis à jour')
      onSuccess(); onClose()
    } catch { toast.error('Erreur lors de la mise à jour') }
    finally { setLoading(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 360, maxWidth: '92vw' }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#0D1117' }}>Ajuster les frais</h3>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            step="0.1" min="0"
            style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid #E5E7EB', fontSize: 15, fontFamily: 'inherit' }}
          />
          <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>TND</span>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>
            {loading ? 'Mise à jour…' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main ──────────────────────────────────────────────────────────────────────

export default function AdminDeliveryDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [showAssign, setShowAssign] = useState(false)
  const [showStatus, setShowStatus] = useState(false)
  const [showPrice,  setShowPrice]  = useState(false)

  const { data: delivery, isLoading } = useQuery<Delivery>({
    queryKey: ['delivery', id],
    queryFn: () => deliveriesAPI.getById(id!),
    enabled: !!id,
    refetchInterval: 30_000,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['delivery', id] })
    queryClient.invalidateQueries({ queryKey: ['deliveries'] })
  }

  const handleCancel = async () => {
    const reason = window.prompt('Motif d\'annulation :')
    if (reason === null) return
    try {
      await deliveriesAPI.updateStatus(delivery!._id, { status: 'cancelled', reason })
      toast.success('Colis annulé')
      invalidate()
    } catch { toast.error('Erreur lors de l\'annulation') }
  }

  const openAssign = () => setShowAssign(true)

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <span style={{ color: '#9CA3AF', fontSize: 14 }}>Chargement…</span>
      </div>
    )
  }

  if (!delivery) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <DashIcon name="alert" size={32} style={{ color: '#EF4444' }} />
        <p style={{ color: '#EF4444', marginTop: 12 }}>Colis introuvable.</p>
        <button className="btn btn-ghost" onClick={() => navigate('/admin/livraisons')}>← Retour</button>
      </div>
    )
  }

  // ── Derived data ─────────────────────────────────────────────────────────────

  type PopulatedExpediteur = { _id?: string; name?: string; phone?: string; company?: { name?: string } }
  const exped = delivery.expediteur as unknown as PopulatedExpediteur | undefined
  const expedName = exped?.company?.name ?? exped?.name ?? '—'
  const expedContact = (exped?.company?.name && exped?.name !== exped?.company?.name) ? exped?.name : undefined

  const livreur = delivery.livreur
  const timeline = delivery.timeline ?? []

  const currentIdx = stepIndexFor(delivery.status)
  const isTerminal  = ['delivered', 'cancelled', 'failed', 'returned', 'unreachable'].includes(delivery.status)
  const isNegative  = ['cancelled', 'failed', 'returned', 'unreachable'].includes(delivery.status)

  const assignedEntry = timeline.find(e => ['accepted', 'assigned'].includes(e.status))
  const enServiceDepuis = assignedEntry ? fmtTime(assignedEntry.timestamp) : '—'

  const vehicleLabel = livreur?.vehicle?.type
    ? livreur.vehicle.type.charAt(0).toUpperCase() + livreur.vehicle.type.slice(1)
    : '—'

  return (
    <>
      {/* Modals */}
      {showAssign && delivery && (
        <AssignLivreurModal
          deliveryId={delivery._id}
          onClose={() => setShowAssign(false)}
          onSuccess={invalidate}
        />
      )}
      {showStatus && (
        <StatusModal
          deliveryId={delivery._id}
          current={delivery.status}
          onClose={() => setShowStatus(false)}
          onSuccess={invalidate}
        />
      )}
      {showPrice && (
        <PriceModal
          deliveryId={delivery._id}
          current={delivery.price?.base ?? 0}
          onClose={() => setShowPrice(false)}
          onSuccess={invalidate}
        />
      )}

      {/* ── Header ─────────────────────────────────────────────────────────────── */}
      <button
        onClick={() => navigate('/admin/livraisons')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 5, padding: '0 0 10px', fontFamily: 'inherit' }}
      >
        <DashIcon name="arrow-left" size={13} /> Tous les colis
      </button>

      <div className="page-head" style={{ marginBottom: 20 }}>
        <div className="ph-titles">
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            Colis <span style={{ color: '#1D9E75' }}>{delivery.trackingCode}</span>
            <StatusBadge status={delivery.status} />
          </h1>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: 'var(--muted)' }}>
            · Créé le {fmtDate(delivery.createdAt)} {fmtTime(delivery.createdAt)}
            {delivery.updatedAt && ` · Mis à jour ${fmtRelative(delivery.updatedAt)}`}
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-ghost" onClick={() => toast('Litige en développement')}>
            <DashIcon name="alert" size={14} /> Litige
          </button>
          <button className="btn btn-ghost" onClick={() => setShowStatus(true)}>
            <DashIcon name="edit" size={14} /> Modifier
          </button>
          <button className="btn btn-primary" onClick={openAssign}>
            <DashIcon name="zap" size={14} /> Ré-assigner
          </button>
        </div>
      </div>

      {/* ── Top row: QR + Suivi ────────────────────────────────────────────────── */}
      <div className="detail-grid" style={{ '--detail-cols': '230px 1fr', gap: 16, marginBottom: 16 } as CSSProperties}>

        {/* QR card */}
        <div style={{
          background: '#fff', border: '2px dashed #E5E7EB', borderRadius: 16, padding: 20,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        }}>
          <ColisQRCode
            trackingCode={delivery.trackingCode}
            contactName={delivery.dropoff.contactName}
            dropoffAddress={delivery.dropoff.address}
            price={delivery.price?.base}
            paymentMethod={delivery.paymentMethod}
            size={160}
            showActions={false}
          />
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: '#0D1117' }}>
                {delivery.trackingCode}
              </span>
              <button
                onClick={() => { navigator.clipboard.writeText(delivery.trackingCode); toast.success('Copié !') }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 2, display: 'flex' }}
              >
                <DashIcon name="copy" size={13} />
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Code de suivi · {delivery.trackingCode}-2026</div>
          </div>
          <div style={{ display: 'flex', gap: 8, width: '100%' }}>
            <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 11, gap: 4 }} onClick={() => window.print()}>
              <DashIcon name="printer" size={12} /> Étiquette
            </button>
            <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 11, gap: 4 }} onClick={() => toast('PDF en développement')}>
              <DashIcon name="download" size={12} /> PDF
            </button>
          </div>
        </div>

        {/* Suivi du colis */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Suivi du colis</span>
            {!isTerminal && (
              <span style={{
                background: '#EFF9F5', border: '1px solid #A7F3D0', color: '#059669',
                fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
              }}>
                ETA · —
              </span>
            )}
          </div>

          {isNegative ? (
            <div style={{
              padding: '16px 20px', background: '#FEF2F2', border: '1px solid #FCA5A5',
              borderRadius: 12, color: '#DC2626',
            }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{STATUS_LABEL[delivery.status]}</div>
              {delivery.cancellation?.reason && (
                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6B7280', fontWeight: 400 }}>
                  {delivery.cancellation.reason}
                </p>
              )}
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              {/* Vertical connector line */}
              <div style={{
                position: 'absolute', left: 11, top: 20, bottom: 20,
                width: 2, background: '#E5E7EB', zIndex: 0,
              }} />
              {TRACKING_STEPS.map((step, idx) => {
                const isDone   = currentIdx > idx
                const isActive = currentIdx === idx
                const entry    = timelineEntryFor(timeline, step.statuses)

                return (
                  <div key={idx} style={{ display: 'flex', gap: 14, marginBottom: 20, position: 'relative', zIndex: 1 }}>
                    {/* Circle */}
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isDone ? '#1D9E75' : isActive ? '#1D9E75' : '#F3F4F6',
                      border: !isDone && !isActive ? '2px solid #E5E7EB' : 'none',
                      boxShadow: isActive ? '0 0 0 4px rgba(29,158,117,0.18)' : 'none',
                      fontSize: 11, color: '#fff', fontWeight: 700,
                    }}>
                      {isDone ? '✓' : isActive ? (
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />
                      ) : null}
                    </div>

                    {/* Content */}
                    <div style={{ paddingTop: 2, flex: 1 }}>
                      <div style={{
                        fontWeight: isDone || isActive ? 700 : 400,
                        fontSize: 13,
                        color: isDone || isActive ? '#0D1117' : '#9CA3AF',
                        marginBottom: 2,
                      }}>
                        {step.label}
                      </div>
                      {entry?.note && (
                        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 1 }}>{entry.note}</div>
                      )}
                      {entry ? (
                        <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                          {fmtDate(entry.timestamp)} · {fmtTime(entry.timestamp)}
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: '#C4C9D4', fontStyle: 'italic' }}>{step.pending}</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Middle row: Détails + Livreur ─────────────────────────────────────── */}
      <div className="detail-grid" style={{ '--detail-cols': '1fr 380px', gap: 16, marginBottom: 16 } as CSSProperties}>

        {/* Détails du colis */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
          <h3 style={{ margin: '0 0 18px', fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Détails du colis</h3>

          {/* Route visual */}
          <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
            {/* Departure */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, marginTop: 3 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#1D9E75', border: '2px solid #059669' }} />
                <div style={{ width: 2, height: 32, marginTop: 4, background: 'repeating-linear-gradient(to bottom, #9CA3AF 0, #9CA3AF 4px, transparent 4px, transparent 8px)' }} />
              </div>
              <div style={{ paddingBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1D9E75', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                  Départ · {expedName}
                </div>
                <div style={{ fontSize: 13, color: '#374151' }}>{delivery.pickup?.address ?? '—'}</div>
              </div>
            </div>
            {/* Destination */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flexShrink: 0, marginTop: 3 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#0D1117', border: '2px solid #374151' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0D1117', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                  Destination · {delivery.dropoff.contactName ?? '—'}
                </div>
                <div style={{ fontSize: 13, color: '#374151' }}>{delivery.dropoff.address}</div>
              </div>
            </div>
          </div>

          {/* Field rows */}
          <FieldRow
            label="Destinataire"
            value={delivery.dropoff.contactName ?? '—'}
            sub={delivery.dropoff.contactPhone}
          />
          <FieldRow
            label="Expéditeur"
            value={expedName}
            sub={[expedContact, exped?.phone].filter(Boolean).join(' · ')}
          />
          {delivery.package?.weight && (
            <FieldRow label="Poids" value={`${delivery.package.weight} kg`} />
          )}
          <FieldRow label="Type" value={[
            delivery.package?.fragile ? 'Fragile' : null,
            delivery.package?.description,
          ].filter(Boolean).join(' · ') || SIZE_LABEL[delivery.package?.size ?? 'medium']} />
          {delivery.paymentMethod === 'cash' && (
            <FieldRow label="COD à collecter" value={`${delivery.price?.base ?? 0} DT`} green strong />
          )}
          <FieldRow
            label="Frais livraison"
            value={`${(delivery.price?.commission ?? 0) > 0 ? delivery.price.commission : delivery.price?.base ?? 0} DT`}
            strong
          />
          {delivery.comment && (
            <FieldRow label="Note expéditeur" value={`« ${delivery.comment} »`} />
          )}
          {delivery.dropoff.notes && (
            <FieldRow label="Note livraison" value={delivery.dropoff.notes} />
          )}
        </div>

        {/* Livreur assigné */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Livreur assigné</h3>
            {livreur?.name && (
              <span style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: '#EFF9F5', border: '1px solid #A7F3D0', color: '#059669',
                fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} />
                En service
              </span>
            )}
          </div>

          {!livreur?.name ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF' }}>
              <DashIcon name="user" size={32} />
              <p style={{ margin: '10px 0 16px', fontSize: 13, fontStyle: 'italic' }}>Aucun livreur assigné</p>
              <button className="btn btn-primary btn-sm" onClick={openAssign}>
                Assigner un livreur
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 20 }}>
                <DashAvatar name={livreur.name} size={48} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0D1117', marginBottom: 3 }}>
                    {livreur.name}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>
                    {livreur._id ? `LV-${String(livreur._id).slice(-4).toUpperCase()}` : ''}
                    {livreur.phone ? ` · ${livreur.phone}` : ''}
                  </div>
                  {(livreur.rating ?? 0) > 0 && (
                    <div style={{ fontSize: 13, color: '#374151', display: 'flex', gap: 4, alignItems: 'center' }}>
                      <span style={{ color: '#F59E0B' }}>★</span>
                      <span style={{ fontWeight: 600 }}>{(livreur.rating ?? 0).toFixed(1)}</span>
                      <span style={{ color: '#9CA3AF' }}>· {livreur.totalDeliveries ?? 0} livraisons</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 8, marginBottom: 20 } as CSSProperties}>
                <InfoChip label="Zone" value="—" />
                <InfoChip label="Véhicule" value={vehicleLabel} />
                <InfoChip label="Plaque" value={livreur.vehicle?.plate ?? '—'} />
                <InfoChip label="En service depuis" value={enServiceDepuis} />
              </div>

              <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 8 } as CSSProperties}>
                <a
                  href={`tel:${livreur.phone}`}
                  className="btn btn-ghost"
                  style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <DashIcon name="phone" size={14} /> Appeler
                </a>
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    if (!livreur.phone) return
                    const p = livreur.phone.replace(/\D/g, '')
                    window.open(`https://wa.me/${p}?text=${encodeURIComponent(`Bonjour, concernant le colis ${delivery.trackingCode}.`)}`, '_blank')
                  }}
                >
                  <DashIcon name="message" size={14} /> Message
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Actions administrateur ─────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 24 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#0D1117' }}>Actions administrateur</h3>
        <p style={{ margin: '0 0 20px', fontSize: 12, color: '#9CA3AF' }}>
          Interventions disponibles uniquement pour les opérations.
        </p>
        <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 10 } as CSSProperties}>
          {([
            { icon: 'zap',     label: 'Forcer ré-assignation',          onClick: openAssign },
            { icon: 'edit',    label: 'Modifier le statut manuellement', onClick: () => setShowStatus(true) },
            { icon: 'receipt', label: 'Régénérer le bordereau',          onClick: () => toast('Feature en développement') },
            { icon: 'dollar',  label: 'Ajuster les frais',              onClick: () => setShowPrice(true) },
            { icon: 'alert',   label: 'Marquer comme litige',           onClick: () => toast('Feature en développement') },
            { icon: 'x',       label: 'Annuler le colis',               onClick: handleCancel, danger: true, disabled: isTerminal },
          ] as { icon: string; label: string; onClick: () => void; danger?: boolean; disabled?: boolean }[]).map((a) => (
            <button
              key={a.label}
              onClick={a.onClick}
              disabled={a.disabled}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px',
                borderRadius: 12, border: `1px solid ${a.danger ? '#FCA5A5' : '#E5E7EB'}`,
                background: a.danger ? '#FEF2F2' : '#F9FAFB',
                color: a.danger ? '#DC2626' : '#374151',
                fontSize: 13, fontWeight: 600,
                cursor: a.disabled ? 'not-allowed' : 'pointer',
                opacity: a.disabled ? 0.45 : 1,
                fontFamily: 'inherit', textAlign: 'left', transition: 'background 0.15s',
              }}
            >
              <DashIcon name={a.icon} size={15} />
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </>
  )
}
